/**
 * 几何换算（P1-1.7）
 *
 * 这个文件是 v2 目录里<b>唯一</b>允许对 `features.*` 做算术的地方，而且只做**单调缩放**：
 * 把数变成像素/百分比，不产生任何分类结论（D12/R16）。
 * 判"够不够"、判"稳定不稳定"都在服务端桶码里；这里若写出 `if (cv < 0.25)` 就是第二份判定逻辑。
 *
 * 所有函数对 `null`/缺值都返回 `null`，调用方按"没有这根条"渲染 —— 不许退回 0，
 * 那等于把"看不见"画成"值为 0"（R2 的老坑）。
 */

/** 非空数值的最大值；全空返回 null（不是 0，否则柱子会被压成 0 高再被读成"没做"） */
export function maxOf(values) {
  const nums = (Array.isArray(values) ? values : [])
    .map((v) => (v === null || v === undefined || v === '' ? null : Number(v)))
    .filter((v) => v !== null && Number.isFinite(v));
  return nums.length ? Math.max(...nums) : null;
}

/** 值占峰值的百分比（0–100）。分母为 0 或缺值时返回 null。 */
export function percentOfPeak(value, peak) {
  const v = value === null || value === undefined ? null : Number(value);
  const p = peak === null || peak === undefined ? null : Number(peak);
  if (v === null || p === null || !Number.isFinite(v) || !Number.isFinite(p)) {
    return null;
  }
  if (p === 0) return null;
  return Math.min(100, Math.max(0, (v / p) * 100));
}

/**
 * 周槽位：`weeklyAttainment[i]` 为 `null` 表示"这一周没被窗口完整覆盖"，
 * 与"覆盖了但值为 0"（用户这周真的没做）是两种画法。混掉就等于把缺的周补 0 ——
 * 正是 V1 六周折线被批评过的那个毛病（决策 ⑨/D15 的由来）。
 */
export function weekSlots(weeklyAttainment) {
  const list = Array.isArray(weeklyAttainment) ? weeklyAttainment : [];
  const peak = maxOf(list);
  return list.map((value, index) => ({
    index,
    // 文案（"第几周"）在 copy.js 的 LABEL 里，这里只给序号：本文件只做几何，不持有字
    available: value !== null && value !== undefined,
    value: value === null || value === undefined ? null : Number(value),
    heightPct: percentOfPeak(value, peak),
  }));
}

/**
 * 基线带在 0–峰值 横轴上的位置（百分比）。缺 p25/p75 或峰值时返回 null ⇒ 不画带子。
 * 轴的峰值来自服务端给的同源特征（mean/p75），所以带子和中线一定在同一把尺上。
 */
export function bandGeometry({ p25, p75, axisPeak }) {
  const left = percentOfPeak(p25, axisPeak);
  const right = percentOfPeak(p75, axisPeak);
  if (left === null || right === null) return null;
  return { leftPct: left, widthPct: Math.max(0, right - left) };
}

/** 中位数在轴上的位置 */
export function markerPct(value, axisPeak) {
  return percentOfPeak(value, axisPeak);
}

/**
 * 一格在“该指标自己窗口峰值”下的量级档位（0..levels-1），只用于上色深浅。
 *
 * 单调缩放，不是阈值分档：深浅不表示“好不好”，只表示“这个数在该指标自己的范围里有多大”。
 * 缺值、峰值为 0 时返回 null —— 调用方按“不上色”渲染，而不是当成最低档。
 */
export function levelOf(value, peak, levels = 4) {
  const ratio = percentOfPeak(value, peak);
  if (ratio === null) return null;
  const level = Math.floor((ratio / 100) * levels);
  return Math.min(levels - 1, Math.max(0, level));
}
